let context: AudioContext | null = null;
export const REST_VOICE_PHRASE = "Time to exercise";
export type RestAlertMode = "voice" | "soft_chime" | "off";

export async function primeChime(): Promise<void> {
  const AudioContextConstructor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) return;
  context ??= new AudioContextConstructor();
  if (context.state === 'suspended') await context.resume();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  gain.gain.value = 0.00001;
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(); oscillator.stop(context.currentTime + 0.01);
}

export async function playGentleChime(): Promise<void> {
  await primeChime();
  if (!context) return;
  const now=context.currentTime;
  for (const [frequency,offset] of [[523.25,0],[659.25,0.7],[783.99,1.4]] as const) {
    const oscillator=context.createOscillator(); const gain=context.createGain();
    oscillator.type='sine'; oscillator.frequency.value=frequency;
    gain.gain.setValueAtTime(0.0001,now+offset); gain.gain.exponentialRampToValueAtTime(0.11,now+offset+0.04); gain.gain.exponentialRampToValueAtTime(0.0001,now+offset+1.25);
    oscillator.connect(gain).connect(context.destination); oscillator.start(now+offset); oscillator.stop(now+offset+1.3);
  }
}

/** Called from the Done set user gesture so iOS can unlock the audio path. */
export async function primeRestAlert(mode: RestAlertMode) {
  if (mode === "off") return;
  await primeChime();
  if (mode === "voice" && "speechSynthesis" in window)
    window.speechSynthesis.getVoices();
}

async function speakRestPhrase() {
  if (
    !("speechSynthesis" in window) ||
    typeof SpeechSynthesisUtterance === "undefined"
  )
    return false;
  return new Promise<boolean>((resolve) => {
    let settled = false;
    let started = false;
    const utterance = new SpeechSynthesisUtterance(REST_VOICE_PHRASE);
    utterance.lang = "en-AU";
    utterance.rate = 0.95;
    utterance.pitch = 1;
    const finish = (spoken: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(startupTimeout);
      clearTimeout(completionTimeout);
      resolve(spoken);
    };
    utterance.onstart = () => {
      started = true;
      clearTimeout(startupTimeout);
    };
    utterance.onend = () => finish(true);
    utterance.onerror = () => finish(false);
    const startupTimeout = window.setTimeout(() => {
      if (!started) finish(false);
    }, 1200);
    const completionTimeout = window.setTimeout(() => finish(false), 5000);
    try {
      window.speechSynthesis.speak(utterance);
    } catch {
      finish(false);
    }
  });
}

export async function playRestAlert(mode: RestAlertMode) {
  if (mode === "off") return "off" as const;
  if (mode === "soft_chime") {
    await playGentleChime();
    return "soft_chime" as const;
  }
  // The default Voice mode intentionally starts both one-shot cues together:
  // a gentle sound remains useful if iOS delays speech, while speech provides
  // the exact hands-free instruction. Neither source loops.
  const [spoken] = await Promise.all([
    speakRestPhrase(),
    playGentleChime().then(() => true).catch(() => false),
  ]);
  return spoken ? ("voice" as const) : ("soft_chime" as const);
}
